class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Delay initialization to ensure DOM and store are ready
    this._initializeComponent();
    
    // Also try initializing after a short delay as fallback
    setTimeout(() => {
      if (!this._initialized) {
        this._initializeComponent();
      }
    }, 500);
  }
  
  _initializeComponent() {
    // Prevent double initialization
    if (this._initialized) return;
    
    this.amountToSpendElement = this.querySelector('amount-to-spend');
    this.amountLeftElement = this.querySelector('amount-left');
    this.moneyValueLeftElement =
      this.amountLeftElement?.querySelector('money-value');
    this.minimumReachedElement = this.querySelector('minimum-reached');
    this.track = this.querySelector('free-shipping-bar-track');
    
    // Check if critical elements exist
    if (!this.track) {
      return;
    }

    this.minimumValue = Number(this.getAttribute('minimum-value'));
    
    // Get initial value from store if available, otherwise from attribute
    if (window.theme?.cart?.store) {
      const currentState = window.theme.cart.store.getState();
      this.value = currentState?.products?.total_price || Number(this.getAttribute('value')) || 0;
    } else {
      this.value = Number(this.getAttribute('value')) || 0;
    }
    
    this._setVariables();
    
    // Force initial update to set the progress bar correctly
    this._update();
    
    // Mark as initialized
    this._initialized = true;

    // Subscribe to store updates
    if (window.theme?.cart?.store) {
      this.unsubscribe = window.theme.cart.store.subscribe((state) => {
        // Always update when cart changes
        const newValue = state.products.total_price;
        console.log('[FreeShippingBar] Store subscription - new value:', newValue, 'state:', state.products);
        this.value = newValue;
        this._update();

      if (
        this.value >= this.minimumValue &&
        this.classList.contains('is-visible')
      ) {
        this.track.addEventListener(
          'transitionend',
          () => {
            setTimeout(() => {
              this._playGoalAnimation();
            }, 250);
          },
          {
            once: true,
          },
        );
      }
    });
    }

    this.revealDelay = this.getAttribute('reveal-delay') || 0;

    setTimeout(() => {
      const goalAnimationElement = this.querySelector(
        'free-shipping-bar-goal-animation',
      );

      if (goalAnimationElement && window.lottie && !this.goalAnimation) {
        this.goalAnimation = lottie.loadAnimation({
          container: goalAnimationElement,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          useWebWorker: false,
          path: goalAnimationElement.getAttribute('data-url'),
        });
      }
    });

    let options = {
      rootMargin: '0px',
      threshold: 1.0,
    };

    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setTimeout(() => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');

            setTimeout(() => {
              if (this.amountLeft === 0) this._playGoalAnimation();
            }, 1000);

            this.visibilityObserver.disconnect();
          }
        }, this.revealDelay);
      });
    }, options);

    this.visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.classList.remove('is-visible');
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.unsubscribe) this.unsubscribe();
    // Reset initialization flag so it can reinitialize when reconnected
    this._initialized = false;
  }

  _setVariables() {
    this.amountLeft =
      this.minimumValue - this.value <= 0 ? 0 : this.minimumValue - this.value;
    this.moneyLeft = window.Shopify.formatMoney(
      this.amountLeft,
      window.theme.money_format,
    ).replace(/[\.,]00$/, '');
    this.displacement = Math.round((this.amountLeft / this.minimumValue) * 100);

    // Debug logging
    console.log('[FreeShippingBar] _setVariables:', {
      minimumValue: this.minimumValue,
      value: this.value,
      amountLeft: this.amountLeft,
      moneyLeft: this.moneyLeft,
      displacement: this.displacement
    });
  }

  _update() {
    // Ensure track element exists before updating
    if (!this.track) {
      this.track = this.querySelector('free-shipping-bar-track');
      if (!this.track) {
        return;
      }
    }
    
    this.setAttribute('value', this.value);
    this._setVariables();

    if (this.moneyValueLeftElement)
      this.moneyValueLeftElement.innerHTML = this.moneyLeft;

    if (this.amountToSpendElement) {
      if (this.value > 0) {
        this.amountToSpendElement.setAttribute('hidden', '');
      } else {
        this.amountToSpendElement.removeAttribute('hidden');
      }
    }

    if (this.amountLeftElement) {
      if (this.value === 0 || this.amountLeft === 0) {
        this.amountLeftElement.setAttribute('hidden', '');
      } else {
        this.amountLeftElement.removeAttribute('hidden');
      }
    }

    if (this.minimumReachedElement) {
      if (this.amountLeft > 0) {
        this.minimumReachedElement.setAttribute('hidden', '');
      } else {
        this.minimumReachedElement.removeAttribute('hidden');
      }
    }

    // Update the progress bar displacement
    this.track.style.setProperty('--displacement', `${this.displacement}%`);
    
    // Force is-visible class if not present (needed for CSS animation)
    if (!this.classList.contains('is-visible')) {
      this.classList.add('is-visible');
    }
    
    // Check if we just reached free shipping threshold
    if (this.amountLeft === 0 && this.previousAmountLeft > 0) {
      setTimeout(() => this._playGoalAnimation(), 500);
    }
    this.previousAmountLeft = this.amountLeft;
  }

  _playGoalAnimation() {
    const animationShown = Boolean(
      localStorage.getItem('freeShippingAnimationShown'),
    );

    if (animationShown) return;

    if (this.goalAnimation) {
      localStorage.setItem('freeShippingAnimationShown', true);
      this.goalAnimation.play();
    }
  }
}
customElements.define('free-shipping-bar', FreeShippingBar);
